var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext } from 'react';
import { Formik } from 'formik';
import { CartIdContext } from '@jetshop/core/components/Cart/CartIdContext';
import { useMutation, useApolloClient } from '@apollo/react-hooks';
import { getAddToCartOptimisticResponse } from '@jetshop/core/components/Mutation/cartMutationUtils';
import t from '@jetshop/intl';
import uniqueId from '@jetshop/core/helpers/uniqueId';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { useTrackProductAdd, getCommentsFromFormikValues, addToCartSuccess, addToCartError, getArticleNumber } from './AddToCart/addToCartUtils';
import throwErrorInDev from '../../helpers/throwErrorInDev';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import ProductConfigurationContext from '../ProductConfigurationProvider/ProductConfigurationContext';
const useAddToCart = ({ articleNumber, cartMutation, cartQuery, onAddToCartInit, onAddToCartSuccess, onAddToCartError, variant, product: productFromOpts }) => {
    const [addToCartMutation] = useMutation(cartMutation, {
        onCompleted: () => localStorage.setItem('cartChange', '1')
    });
    const client = useApolloClient();
    const selectedVariation = variant;
    const product = productFromOpts;
    const configContext = useContext(ProductConfigurationContext);
    const configurationIds = configContext === null || configContext === void 0 ? void 0 : configContext.ids;
    const trackProductVariant = useTrackProductAdd(selectedVariation);
    const { cartId, setCartId } = useContext(CartIdContext);
    const price = useDynamicPrice(product, selectedVariation);
    const onSubmit = (values) => {
        trackProductVariant(product, values.quantity);
        const mutationId = uniqueId();
        const preOrderDate = values.preOrder ? new Date(values.preOrder) : null;
        const callbackOptions = {
            product,
            price,
            quantity: values.quantity,
            selectedVariation,
            mutationId
        };
        // Call the callback
        onAddToCartInit && onAddToCartInit(callbackOptions);
        const comments = getCommentsFromFormikValues(values);
        return addToCartMutation({
            variables: {
                input: {
                    articleNumber,
                    cartId,
                    quantity: values.quantity,
                    comments,
                    configurationIds,
                    preOrderDate
                }
            },
            optimisticResponse: {
                addToCart: {
                    cart: getAddToCartOptimisticResponse(product, values.quantity, cartId, client, cartQuery),
                    __typename: 'CartMutation'
                }
            }
        })
            .then(addToCartSuccess({
            onAddToCartSuccess,
            callbackOptions,
            cartId,
            setCartId
        }))
            .catch(addToCartError({ onAddToCartError, callbackOptions }));
    };
    return {
        onSubmit
    };
};
const useValidate = ({ commentValidators, articleNumber, product, variant, getMissingOptions }) => {
    var _a, _b;
    const { status, maxOrderQuantity } = useStockStatus(variant || product);
    const isPreOrderRequired = !!((_a = product.preOrder) === null || _a === void 0 ? void 0 : _a.fromDate) && !!((_b = product.preOrder) === null || _b === void 0 ? void 0 : _b.toDate);
    const validate = (values) => {
        const errors = {};
        if (status !== 'inStock') {
            errors.status = t('Product is not in stock');
        }
        if (values.quantity < 1) {
            errors.quantity = t('Please enter a valid quantity');
        }
        if (maxOrderQuantity && values.quantity > maxOrderQuantity) {
            errors.quantity = t('{maxOrderQuantity} is the maximum order quantity', {
                maxOrderQuantity
            });
        }
        Object.keys(values.comments).forEach(name => {
            const validator = commentValidators[name];
            if (validator) {
                const error = validator({ value: values.comments[name] });
                if (error) {
                    if (typeof errors.comments === 'undefined') {
                        errors.comments = {};
                    }
                    errors.comments[name] = error;
                }
            }
            if (product.customerComments.find(customerComment => customerComment.name === name).required &&
                values.comments[name].length === 0) {
                if (typeof errors.comments === 'undefined') {
                    errors.comments = {};
                }
                errors.comments[name] = t(`{customerCommentName} is required`, {
                    customerCommentName: name
                });
            }
        });
        if (!articleNumber) {
            if (typeof errors.options === 'undefined') {
                errors.options = {};
            }
            const missingOptions = getMissingOptions();
            missingOptions.forEach(option => {
                errors.options[option.name] = t(`{optionName} is required`, {
                    optionName: option.name
                });
            });
        }
        if (!values.preOrder && isPreOrderRequired) {
            errors.preOrder = t('Pre-order date is required');
        }
        return errors;
    };
    return { validate };
};
const AddToCartFormik = (_a) => {
    var { children, onAddToCartInit, onAddToCartError, onAddToCartSuccess, cartQuery, cartMutation, initialValues, variant, product, commentValidators = {}, getMissingOptions } = _a, props = __rest(_a, ["children", "onAddToCartInit", "onAddToCartError", "onAddToCartSuccess", "cartQuery", "cartMutation", "initialValues", "variant", "product", "commentValidators", "getMissingOptions"]);
    throwErrorInDev(!cartQuery, `You forgot to pass 'cartQuery' to AddToCartForm`);
    throwErrorInDev(!cartMutation, `You forgot to pass 'cartMutation' to getSetQuantityOptimisticResponse`);
    const selectedVariation = variant;
    const articleNumber = getArticleNumber(product, selectedVariation);
    const initialComments = {};
    product.customerComments &&
        product.customerComments.forEach(({ name }) => {
            initialComments[name] = '';
        });
    const initialOptions = {};
    product.variants.options.forEach(option => {
        initialOptions[option.name] = '';
    });
    const combinedInitialValues = Object.assign({ quantity: 1, comments: initialComments, options: initialOptions }, initialValues);
    const { validate } = useValidate({
        commentValidators,
        articleNumber,
        product,
        variant,
        getMissingOptions
    });
    const { onSubmit } = useAddToCart({
        articleNumber,
        cartMutation,
        cartQuery,
        onAddToCartInit,
        onAddToCartSuccess,
        onAddToCartError,
        product,
        variant
    });
    return (React.createElement(Formik, Object.assign({ validateOnChange: false, initialValues: combinedInitialValues, validate: validate, onSubmit: onSubmit, render: renderProps => children(renderProps) }, props)));
};
export default AddToCartFormik;
//# sourceMappingURL=AddToCartFormik.js.map